import React, { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, Btn, Input, Select, Textarea } from "../UI";
import { errMsg, valueLabel, FORMATS, THEMES } from "./format";

const EMPTY = {
  name: "", promotion_id: "", starts_at: "", ends_at: "", venue: "", booth: "", city: "",
  headline: "", subline: "", card_theme: "dark", card_format: "business", staff: "", initial_codes: 50,
};

export const EventCreateModal = ({ promos, onClose, onCreated }) => {
  const active = (promos || []).filter((p) => p.active);
  const [f, setF] = useState({ ...EMPTY, promotion_id: active[0]?.id || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Give the trade show a name");
    if (!f.promotion_id) return toast.error("Pick the promotion these cards will carry");
    setBusy(true);
    try {
      const payload = {
        ...f, starts_at: f.starts_at || null, ends_at: f.ends_at || null,
        staff: f.staff.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        initial_codes: parseInt(f.initial_codes) || 0,
      };
      const { data } = await api.post("/promotions/events", payload);
      toast.success(`Trade show created — ${data.codes_created} code(s) allocated`);
      onCreated(data.event);
    } catch (e) { toast.error(errMsg(e, "Could not create the trade show")); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-create-modal">
      <Card className="w-full max-w-2xl !p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-bold">New trade show</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="event-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Trade show name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="CPAC 2026 · Booth 412" data-testid="event-name-input" />
          <Select label="Promotion on the cards" value={f.promotion_id} onChange={(e) => set("promotion_id", e.target.value)} data-testid="event-promo-select">
            {active.length === 0 && <option value="">No active promotions — create one first</option>}
            {active.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${valueLabel(p)}`}</option>)}
          </Select>
          <Input label="Starts" type="date" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="event-starts-input" />
          <Input label="Ends" type="date" value={f.ends_at} onChange={(e) => set("ends_at", e.target.value)} data-testid="event-ends-input" />
          <Input label="Venue" value={f.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Gaylord National" data-testid="event-venue-input" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Booth" value={f.booth} onChange={(e) => set("booth", e.target.value)} placeholder="412" data-testid="event-booth-input" />
            <Input label="City" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Washington, DC" data-testid="event-city-input" />
          </div>
          <Input label="Card headline" value={f.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Defaults to the promotion name" data-testid="event-headline-input" />
          <Input label="Initial codes to allocate" type="number" min="0" max="500" value={f.initial_codes} onChange={(e) => set("initial_codes", e.target.value)} data-testid="event-initial-codes-input" />
          <Select label="Card format" value={f.card_format} onChange={(e) => set("card_format", e.target.value)} data-testid="event-format-select">
            {FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
          <Select label="Card look" value={f.card_theme} onChange={(e) => set("card_theme", e.target.value)} data-testid="event-theme-select">
            {THEMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </div>
        <Textarea label="Card subline (shown under the value)" value={f.subline} onChange={(e) => set("subline", e.target.value)} rows={2} placeholder="Defaults to the promotion description" data-testid="event-subline-input" />
        <Textarea label="Booth staff (comma or newline separated — for hand-out attribution)" value={f.staff} onChange={(e) => set("staff", e.target.value)} rows={2} placeholder="Jane Doe, Marcus Lee" data-testid="event-staff-input" />
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || active.length === 0} data-testid="event-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create trade show
          </Btn>
        </div>
      </Card>
    </div>
  );
};
